import React, { useState, useMemo, useEffect } from 'react';
import { getNearestValue } from '../../../lib/ConstList';

const MaskRange = ({
  id,
  bandStart,
  bandEnd,
  height,
  xScale,
  onClick,
  onMouseMove,
  onMouseUp,
  isSelected,
}) => {
  const x = useMemo(() => {
    return xScale(Math.min(bandStart, bandEnd));
  }, [bandStart, bandEnd, xScale]);

  const width = useMemo(() => {
    return xScale(Math.max(bandStart, bandEnd)) - x;
  }, [bandStart, bandEnd, xScale, x]);

  return (
    <g
      key={id}
      clipPath={`url(#chart-clip-plot)`}
      onClick={onClick}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={{ cursor: isSelected ? "move" : "pointer" }}
    >
      <rect
        x={x}
        y={0}
        width={width}
        height={height}
        fill={"gray"}
        fillOpacity={isSelected ? 0.8 : 0.6}
        stroke='black'
        strokeOpacity={isSelected ? 1 : 0}
        strokeWidth={1}
      />
    </g>
  );
}

const EditMaskRange = ({
  id,
  bandStart,
  bandEnd,
  height,
  xScale,
  leftHandleStart,
  CenterHandleStart,
  rightHandleStart,
  onMouseMove,
  onMouseUp,
}) => {
  const x = useMemo(() => {
    return xScale(Math.min(bandStart, bandEnd));
  }, [bandStart, bandEnd, xScale]);

  const width = useMemo(() => {
    return xScale(Math.max(bandStart, bandEnd)) - x;
  }, [bandStart, bandEnd, xScale, x]);

  return (
    <g>
      <g
        key={`edit-${id}`}
        clipPath={`url(#chart-clip-plot)`}
        onMouseMove={onMouseMove}
        onMouseDown={CenterHandleStart}
        onMouseUp={onMouseUp}
        style={{ cursor: "move" }}
      >
        <rect
          x={x}
          y={0}
          width={width}
          height={height}
          fill={"gray"}
          fillOpacity="0.8"
          stroke='black'
          strokeOpacity="1"
          strokeWidth={1}
        />
      </g>

      {/* 左端のハンドル */}
      <g
        key={`edit-left-${id}`}
        clipPath={`url(#chart-clip-plot)`}
        onMouseDown={leftHandleStart}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        style={{ cursor: "col-resize" }}
      >
        <rect
          x={x - 5}
          y={0}
          width={10}
          height={height}
          fill="transparent"
        />
      </g>

      {/* 右端のハンドル */}
      <g
        key={`edit-right-${id}`}
        clipPath={`url(#chart-clip-plot)`}
        onMouseDown={rightHandleStart}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        style={{ cursor: "col-resize" }}
      >
        <rect
          x={x + width - 5}
          y={0}
          width={10}
          height={height}
          fill="transparent"
        />
      </g>
    </g>
  );
}

function MaskGraph({
  xScale,
  xMax,
  yMax,
  margin,
  xRange,
  selectedMaskSetting,
  setSelectedMaskSetting,
  maskSetting,
  editMaskSetting,
  setEditMaskSetting,
  onMaskStatusChange,
  location,
  maxSettings,
  isReadOnly = false,
}) {
  // 新規マスク設定
  const [newMask, setNewMask] = useState(null);

  // ドラッグ中フラグ null | new | edit-left | edit-right | edit
  const [draggingType, setDraggingType] = useState(null);

  // マウスの座標からグラフ上での座標に変換
  const getX = (e) => {
    const x = e.clientX - margin.left * 1.7 - margin.right;
    return xScale.invert(x);
  }

  const onClickMask = (mask) => {
    setSelectedMaskSetting(mask);
  }

  const onDragStartNewMask = (e) => {
    if (isReadOnly) return;

    // 設定数がmaxSettingsを超えている場合は、設定しない
    if (maxSettings && maxSettings <= editMaskSetting.length) {
      console.warn("マスク設定数が上限に達しています。");
      return;
    }

    const x = getNearestValue(getX(e));

    setNewMask({
      start: x,
      end: x,
    });

    setDraggingType("new");

    setTimeout(() => {
      setSelectedMaskSetting(null);
    }, 100);
  }

  const onDraggingMask = (e) => {
    if (isReadOnly) return;
    
    if (draggingType) {
      console.debug('onMouseMove', "ドラッグ中");
      const x = getNearestValue(getX(e));

      // 編集中のマスクがある場合
      if (selectedMaskSetting && draggingType === "edit-left") {
        setSelectedMaskSetting(prev => {
          return {
            ...prev,
            start: x
          };
        });
      } else if (selectedMaskSetting && draggingType === "edit-right") {
        setSelectedMaskSetting(prev => {
          return {
            ...prev,
            end: x
          };
        });
      } else if (selectedMaskSetting && draggingType === "edit") {
        // 並行移動
        setSelectedMaskSetting(prev => {
          const dx = x - prev.clickPos;

          const start = getNearestValue(prev.initStart + dx);
          const end = getNearestValue(prev.initEnd + dx);

          // 範囲外に出る場合
          if (start < xRange.min || end > xRange.max) {
            // 何もしない
            return prev;
          }

          return {
            ...prev,
            start: getNearestValue(prev.initStart + dx),
            end: getNearestValue(prev.initEnd + dx),
          };
        });
      } else if (newMask && draggingType === "new") {
        // 新規マスク設定の範囲を更新
        setNewMask(prev => {
          return {
            ...prev,
            end: x
          };
        });
      }
    }
  }

  const onDragEndMask = (e) => {
    if (isReadOnly) return;
    
    if (draggingType) {
      const x = getNearestValue(getX(e));

      if (selectedMaskSetting && draggingType === "edit-left") {
        const mask = {
          start: Math.min(x, selectedMaskSetting.end),
          end: Math.max(x, selectedMaskSetting.end),
          id: selectedMaskSetting.id,
        }

        setSelectedMaskSetting(mask);

        setEditMaskSetting((prev) => {
          return prev.map((prevMask) => {
            if (prevMask.id === selectedMaskSetting.id) {
              return mask;
            }
            return prevMask;
          });
        });
      } else if (selectedMaskSetting && draggingType === "edit-right") {
        const mask = {
          start: Math.min(x, selectedMaskSetting.start),
          end: Math.max(x, selectedMaskSetting.start),
          id: selectedMaskSetting.id,
        }

        setSelectedMaskSetting(mask);

        setEditMaskSetting((prev) => {
          return prev.map((prevMask) => {
            if (prevMask.id === selectedMaskSetting.id) {
              return mask;
            }
            return prevMask;
          });
        });
      } else if (selectedMaskSetting && draggingType === "edit") {
        const dx = x - selectedMaskSetting.clickPos;
        const x1 = getNearestValue(selectedMaskSetting.initStart + dx);
        const x2 = getNearestValue(selectedMaskSetting.initEnd + dx);

        const mask = {
          start: Math.min(x1, x2),
          end: Math.max(x1, x2),
          id: selectedMaskSetting.id,
        }

        // 範囲外に出る場合
        if (mask.start < xRange.min) {
          mask.start = xRange.min;
          mask.end = getNearestValue(xRange.min + (selectedMaskSetting.end - selectedMaskSetting.start));
        } else if (mask.end > xRange.max) {
          mask.end = xRange.max;
          mask.start = getNearestValue(xRange.max - (selectedMaskSetting.end - selectedMaskSetting.start));
        }

        // 範囲が0の場合は追加しない
        if (mask.start !== mask.end) {
          setSelectedMaskSetting(mask);

          setEditMaskSetting((prev) => {
            return prev.map((prevMask) => {
              if (prevMask.id === selectedMaskSetting.id) {
                return mask;
              }
              return prevMask;
            });
          });
        } else {
          // 範囲が0の場合は削除
          setEditMaskSetting((prev) => {
            return prev.filter((prevMask) => prevMask.id !== selectedMaskSetting.id);
          });

          setSelectedMaskSetting(null);
          setDraggingType(null);
        }
      } else if (newMask && draggingType === "new") {
        const result = {
          start: Math.min(newMask.start, x),
          end: Math.max(newMask.start, x),
          id: crypto.randomUUID(),
        }

        if (result.start !== result.end) {
          // 範囲が0の場合は追加しない
          setEditMaskSetting((prev) => {
            return [...prev, result];
          });

          setSelectedMaskSetting(result);
        }
        setNewMask(null);
      }

      setDraggingType(null);
    }
  }

  useEffect(() => {
    const status = {
      event: "new",
      mask: newMask,
    }

    // 新規追加
    if (newMask != null) {
      status.event = "new";
      status.mask = newMask;
    } else if (selectedMaskSetting) {
      // 編集中
      status.event = "edit";
      status.mask = selectedMaskSetting;
    } else {
      // 編集終了
      status.event = null;
      status.mask = null;
    }

    onMaskStatusChange(status);
  }, [newMask, selectedMaskSetting]);

  useEffect(() => {
    setSelectedMaskSetting(null);
    setNewMask(null);
    setDraggingType(null);
  }, [location, maskSetting]);
    
  return (
    <>
      {/* 範囲外の要素に出た場合にイベントを受ける要素 */}
      <rect
        x={xScale(xRange.min) - 50}
        y={-50}
        width={xScale(xRange.max) - xScale(xRange.min) + 100}
        height={yMax + 100}
        fill="transparent"
        fillOpacity={0.5}
        onMouseEnter={!isReadOnly ? onDragEndMask : undefined}
      />

      {/* 新規マスク設定イベント要素 */}
      <rect
        x={xScale(xRange.min)}
        y={0}
        width={xScale(xRange.max) - xScale(xRange.min)}
        height={yMax}
        fill="transparent"
        onMouseDown={!isReadOnly ? onDragStartNewMask : undefined}
        onMouseMove={!isReadOnly ? onDraggingMask : undefined}
        onMouseUp={!isReadOnly ? onDragEndMask : undefined}
        style={{ cursor: isReadOnly ? "default" : "crosshair" }}
      />

      {/* 新規ドラッグ中のマスク */}
      {newMask && (
        <>
          <MaskRange
            id={"new"}
            bandStart={newMask.start}
            bandEnd={newMask.end}
            xScale={xScale}
            height={yMax}
            onMouseMove={onDraggingMask}
            onMouseUp={onDragEndMask}
            isSelected
          />
        </>
      )}

      {/* 既存のマスク */}
      {editMaskSetting.map((mask, i) => {
        // 編集中のマスクと同じ場合は表示しない
        if (selectedMaskSetting && mask.id === selectedMaskSetting.id) {
          return null;
        }

        return (
          <MaskRange
            id={`mask-${mask.id}`}
            bandStart={mask.start}
            bandEnd={mask.end}
            xScale={xScale}
            height={yMax}
            onClick={!isReadOnly ? () => onClickMask(mask) : undefined}
            onMouseMove={!isReadOnly ? onDraggingMask : undefined}
            onMouseUp={!isReadOnly ? onDragEndMask : undefined}
            isSelected={false}
          />
        );
      })}

      {/* 編集中のマスク */}
      {selectedMaskSetting && (
        <>
          <EditMaskRange
            id={selectedMaskSetting.id}
            bandStart={selectedMaskSetting.start}
            bandEnd={selectedMaskSetting.end}
            xScale={xScale}
            height={yMax}
            leftHandleStart={(e) => {
              console.log("左端をドラッグ開始");
              const x = getNearestValue(getX(e));

              setSelectedMaskSetting(prev => {
                return {
                  ...prev,
                  start: x
                };
              });

              setDraggingType("edit-left");
            }}
            CenterHandleStart={(e) => {
              console.log("中心をドラッグ開始");
              const x = getNearestValue(getX(e));

              setSelectedMaskSetting(prev => {
                return {
                  ...prev,
                  // 最初にクリックした位置を保持
                  clickPos: x,
                  initStart: prev.start,
                  initEnd: prev.end,
                };
              });

              setDraggingType("edit");
            }}
            rightHandleStart={(e) => {
              console.log("右端をドラッグ開始");
              const x = getNearestValue(getX(e));

              setSelectedMaskSetting(prev => {
                return {
                  ...prev,
                  end: x
                };
              });

              setDraggingType("edit-right");
            }}
            onMouseMove={onDraggingMask}
            onMouseUp={onDragEndMask}
          />
        </>
      )}

      {/* 削除ボタン グラフのm着上m着上 */}
      {selectedMaskSetting && !isReadOnly && (
        <g
          onClick={() => {
            setEditMaskSetting((prev) => {
              return prev.filter((mask) => mask.id !== selectedMaskSetting.id);
            });
            setSelectedMaskSetting(null);
          }}
          style={{
            cursor: "pointer",

          }}
        >
          <rect
            x={xMax - 130}
            y={10}
            width="120"
            height="36"
            rx="6"
            ry="6"
            fill="red"
          />

          <text
            x={xMax - 70}
            y={30}
            fill="white"
            fontSize="16"
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            マスクを削除
          </text>
        </g>
      )}
    </>
  );
}

export default MaskGraph;
